import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { useEffect, useRef } from "react"
import _ from "lodash"

// Helper to recursively update nested items by id
const updateNestedById = (data, updatedItem) => {
  if (_.isArray(data)) {
    return data.map(item => updateNestedById(item, updatedItem))
  }

  if (_.isPlainObject(data)) {
    if (data.id === updatedItem.id) return updatedItem
    return _.mapValues(data, val => updateNestedById(val, updatedItem))
  }

  return data
}

// -------------------- FETCH PAGINATED --------------------
export const usePaginatedResource = (
  key,
  fetchFn,
  params = {},
  enabled = true
) => {
  const query = useQuery({
    queryKey: [key, "all", JSON.stringify(params)],
    queryFn: () => fetchFn(params),
    keepPreviousData: true,
    enabled: enabled && !!fetchFn,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnMount: true,
  })

  const prevParamsRef = useRef(params)

  useEffect(() => {
    const prevParams = prevParamsRef.current
    const hasChanged = JSON.stringify(prevParams) !== JSON.stringify(params)

    if (enabled && hasChanged) {
      query.refetch()
      prevParamsRef.current = params
    }
  }, [params, enabled])

  return query
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useResourceItem = (key, fetchFn, id, enabled = true) => {
  return useQuery({
    queryKey: [key, "view", id],
    queryFn: () => fetchFn(id),
    enabled: !!id && enabled,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  })
}

export const useResourceEditItem = (key, fetchFn, id) => {
  return useQuery({
    queryKey: [key, "view", id],
    queryFn: () => fetchFn(id),
    enabled: !!id,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  })
}

// -------------------- CREATE --------------------
export const useCreateResource = (mutationFn, key) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: res => {
      const newItem = res?.data
      if (newItem) {
        queryClient.setQueryData([key, newItem.id], newItem)
      }

      queryClient.invalidateQueries({ queryKey: [key, "all"] })
    },
  })
}

// -------------------- EDIT --------------------
export const useEditResource = (mutationFn, key) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (_, variables) => {
      // Invalidate everything under this resource
      queryClient.invalidateQueries({
        queryKey: [key],
        refetchType: "active",
      })
    },
  })
}

// -------------------- DELETE --------------------
export const useDeleteResource = (mutationFn, key) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: (_, { id }) => {
      queryClient.removeQueries({ queryKey: [key, id], exact: true })
      queryClient.invalidateQueries({ queryKey: [key, "all"] })
    },
  })
}

// -------------------- FETCH VIEW RESOURCE (NO ID) --------------------
export const useResourceView = (key, fetchFn, enabled = true) => {
  return useQuery({
    queryKey: [key, "view"], // no ID, just "view"
    queryFn: () => fetchFn(),
    enabled,
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
  })
}
